import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { ButtonPrimary } from "../button/ButtonCustoms";
import CustomStatus from "../chip/CustomStatus";
import { FiberManualRecord } from "@mui/icons-material";
import { alertActive } from "../alert/swal";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { routeName } from "../../routes/routeName";
import { colors } from "../../constants/colors";
import { useTranslation } from "react-i18next";

type colorMode =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

type DetailCardProps = {
  fullname: string;
  email: string;
  maxStudents: string;
  requestCount: string;
  advisorCount: string;
  isSelect: boolean;
  //
  degree: string;
  faculty: string;
  year: string;
  classRoom: string;
  status:
    | "default"
    | "pending"
    | "transfer_old_pending"
    | "transfer_new_pending"
    | "approve"
    | "advisor_1"
    | "transfer_1"
    | "transfer_2"
    | "advisor_2"
    | "select_transfer"
    | "reject"
    | "end";

  setIsShowModalSendRequest: (isOpen: boolean) => void;
  remark?: string;
  onCancelRequest?: () => void;
  image: string;
  id_advisor_request?: number;
  isTransfer?: boolean;
  isSelectAdvisorTransfer?: boolean;
  onSetAdvisor?: () => void;
  dataRequestTransfer?: any;
  onCancelAdvisor?: () => void;
};

const DetailCard: React.FC<DetailCardProps> = ({
  fullname,
  email,
  maxStudents,
  requestCount,
  advisorCount,
  isSelect,
  status,
  setIsShowModalSendRequest,
  remark,
  onCancelRequest,
  image,
  id_advisor_request,
  isTransfer = false,
  isSelectAdvisorTransfer = false,
  onSetAdvisor,
  dataRequestTransfer,
  onCancelAdvisor,
  degree,
  faculty,
  classRoom,
}) => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const handleStatus = (
    type: string
  ): { statusLabel: string; statusColor: colorMode } => {
    switch (type) {
      case "default":
        return {
          statusLabel: "",
          statusColor: "default",
        };
      case "pending":
        return {
          statusLabel: t("รอพิจารณา"),
          statusColor: "warning",
        };
      case "transfer_old_pending":
        return {
          statusLabel: t("รออาจารย์ที่ปรึกษาพิจารณาคำร้อง"),
          statusColor: "warning",
        };
      case "transfer_new_pending":
        return {
          statusLabel: t("รออาจารย์ที่ปรึกษาใหม่พิจารณาคำร้อง"),
          statusColor: "warning",
        };

      case "approve":
        return {
          statusLabel: t("ตอบรับเป็นที่ปรึกษา"),
          statusColor: "success",
        };
      case "advisor_1":
        return {
          statusLabel: t("อาจารย์ที่ปรึกษาหลัก"),
          statusColor: "info",
        };
      case "advisor_2":
        return {
          statusLabel: t("อาจารย์ที่ปรึกษาร่วม"),
          statusColor: "info",
        };
      case "reject":
        return {
          statusLabel: t("ปฏิเสธคำขอ"),
          statusColor: "error",
        };
      case "end":
        return {
          statusLabel: t("สิ้นสุดการเป็นอาจารย์ที่ปรึกษา"),
          statusColor: "error",
        };
      default:
        return {
          statusLabel: "",
          statusColor: "default",
        };
    }
  };

  const { statusLabel, statusColor } = handleStatus(status);

  return (
    <Card
      sx={{
        boxShadow: `0px 2px 12px 2px ${
          status === "transfer_2" ? colors.subPrimary : "rgba(0, 0, 0, 0.1)"
        }`, // Custom shadow color
        padding: 2,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        borderRadius: "12px",
        border: `2px solid ${
          status === "transfer_2" ? colors.subPrimary : "none"
        }`, // Custom border color
        height: "210px",
        width:
          status === "transfer_1" || status === "transfer_2" ? "425px" : "100%",
      }}
    >
      <Card
        sx={{
          boxShadow: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginRight: 2,
          padding: 1,
          height: "100%",
          borderRadius: "12px",
        }}
      >
        <Box
          component="img"
          src={
            image
              ? process.env.REACT_APP_FILE_BASE_API + "/" + image
              : "https://via.placeholder.com/140"
          }
          alt="Profile"
          sx={{
            width: 140,
            height: 140,
            borderRadius: "12px",
            objectFit: "cover",
          }}
        />
      </Card>

      <Box sx={{ flex: 1 }}>
        <CardContent sx={{ padding: "8px !important" }}>
          {status === "transfer_1" ? (
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 1, color: colors.subPrimary }}
            >
              {t("อาจารย์ที่ปรึกษาปัจจุบัน")}
            </Typography>
          ) : status === "transfer_2" ? (
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 1, color: colors.subPrimary }}
            >
              {t("อาจารย์ที่ปรึกษาใหม่ที่ต้องการ")}
            </Typography>
          ) : null}

          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            {fullname.length > 20
              ? `${fullname.substring(0, 20)}...`
              : fullname}{" "}
            {!isTransfer ? (
              <>
                {status !== "default" ? (
                  <CustomStatus
                    label={statusLabel}
                    type="outlined"
                    color={statusColor}
                    icon={<FiberManualRecord />}
                  />
                ) : null}
              </>
            ) : null}
            {status === "reject" && remark ? (
              <span
                style={{
                  color: "red",
                  marginLeft: "12px",
                }}
              >
                {t("หมายเหตุ :")} {remark}
              </span>
            ) : null}
          </Typography>

          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            {email}
          </Typography>
          {status === "advisor_1" || status === "advisor_2" ? (
            <>
              <Typography
                color="text.secondary"
                sx={{ fontWeight: "bold", fontSize: 10 }}
              >
                {t("ประจำชั้น:")} "-"
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("ระดับ :")} {degree}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("หลักสูตร :")} {faculty}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("ชั้นปีที่ :")} "-"
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("ห้อง :")} {classRoom}
              </Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("จำนวนที่รับนักศึกษาได้สูงสุด :")} {maxStudents ?? 0}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("จำนวนคำร้อง :")} {requestCount ?? 0}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                {t("จำนวนนักศึกษาที่รับเป็นที่ปรึกษา :")} {advisorCount ?? 0}
              </Typography>
            </>
          )}

          {!isTransfer ? (
            <>
              {status === "pending" ? (
                <ButtonPrimary
                  onClick={() => {
                    onCancelRequest && onCancelRequest();
                  }}
                  mode={"cancel"}
                  buttonName={t("ยกเลิก")}
                  btnProps={{
                    width: "200px",
                  }}
                />
              ) : status === "approve" || status === "transfer_old_pending" ? (
                <>
                  <ButtonPrimary
                    onClick={() => {
                      if (id_advisor_request) {
                        navigate(
                          routeName.student.TransferAdvisor +
                            `/${id_advisor_request}`
                        );
                      }
                    }}
                    disabled={status !== "approve"}
                    mode={"change_advisor"}
                    buttonName={t("ส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษาวิทยานิพนธ์")}
                    btnProps={{
                      width: "450px",
                    }}
                  />
                  {/*  */}
                  <ButtonPrimary
                    onClick={() => {
                      if (id_advisor_request) {
                        navigate(
                          routeName.student.TransferAdvisor + `/history`
                        );
                      }
                    }}
                    mode={"history_request"}
                    buttonName={
                      t("ประวัติการส่งคำร้องขอเปลี่ยนอาจารย์ที่ปรึกษาวิทยานิพนธ์")
                    }
                    btnProps={{
                      width: "450px",
                      margin: "0px 0px 0px 12px",
                    }}
                  />
                </>
              ) : status === "default" ? (
                <>
                  {dataRequestTransfer && dataRequestTransfer?.id_advisor ? (
                    <ButtonPrimary
                      onClick={() => {
                        onCancelAdvisor && onCancelAdvisor();
                      }}
                      mode={"del"}
                      buttonName={t("ยกเลิก")}
                      btnProps={{
                        width: "200px",
                      }}
                    />
                  ) : (
                    <ButtonPrimary
                      onClick={() => {
                        if (isSelectAdvisorTransfer) {
                          onSetAdvisor && onSetAdvisor();
                        } else {
                          setIsShowModalSendRequest(true);
                        }
                      }}
                      mode={isSelectAdvisorTransfer ? "next" : "send"}
                      buttonName={
                        isSelectAdvisorTransfer ? t("เลือก") : t("ส่งคำร้องขอ")
                      }
                      disabled={!isSelect}
                      btnProps={{
                        width: "200px",
                      }}
                    />
                  )}
                </>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Box>
    </Card>
  );
};

export default DetailCard;
